import { Activity, ExternalLink } from 'lucide-react'
import { useOutletContext } from 'react-router'

import { porcentaje, useDisponibilidad } from '../api/monitoreo'
import { Aviso, Esqueleto, Pastilla, Tarjeta, Titulo } from '../componentes/ui'
import type { Contexto } from './Modulo'

/**
 * «Disponibilidad» de una web (11-10-2026): si responde, desde cuándo no, su disponibilidad de 24 h, 7
 * y 30 días, el tiempo de respuesta de las últimas 24 h y las últimas caídas. Lo mide MonitoraWeb desde
 * fuera, cada minuto en los planes de pago y cada cinco en el Gratis; el núcleo lo trae
 * (`/api/panel/monitoreo`). Cuando la web cae, el propietario recibe un correo, y otro cuando vuelve.
 */
const ESTADOS = {
  'en-linea': { clave: 'publicado', texto: 'En línea' },
  caida: { clave: 'fallida', texto: 'No responde' },
  comprobando: { clave: 'construyendo', texto: 'Comprobando' },
  pausada: { clave: 'sin-publicar', texto: 'En pausa' },
} as const

const fechaYHora = (iso: null | string) =>
  iso ? new Date(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'

const duracion = (minutos: number) => (minutos < 60 ? `${minutos} min` : `${Math.floor(minutos / 60)} h ${minutos % 60} min`)

export const Disponibilidad = () => {
  const { sitio } = useOutletContext<Contexto>()
  const { data, isPending, error } = useDisponibilidad(sitio?.id ?? 0, Boolean(sitio))
  if (!sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  const titulo = (
    <Titulo descripcion="Comprobamos desde fuera que tu web responde, cada minuto, y te escribimos si deja de hacerlo." rotulo={sitio.nombre}>
      Disponibilidad
    </Titulo>
  )
  if (isPending) return (<>{titulo}<Esqueleto /></>)
  if (error) return (<>{titulo}<Aviso tono="error">No hemos podido leer la disponibilidad. Vuelve a intentarlo en un momento.</Aviso></>)
  if (!data.disponible) return (<>{titulo}<Aviso tono="info">El seguimiento de las webs todavía no está encendido en esta instalación.</Aviso></>)
  if (!data.publicada) return (<>{titulo}<Aviso tono="info">Cuando publiques tu web, empezaremos a comprobar que responde y lo verás aquí.</Aviso></>)
  const m = data.monitor
  if (!m) return (<>{titulo}<Aviso tono="info">{data.sinDatos ? 'Ahora mismo no podemos leer los datos del seguimiento. Vuelve a mirar en un rato.' : 'Tu web acaba de publicarse: en unos minutos verás aquí su primera comprobación.'}</Aviso></>)

  const estado = ESTADOS[m.estado]
  const maximo = Math.max(1, ...m.serie.map((t) => t.mediaMs ?? 0))
  return (
    <>
      {titulo}
      <div className="flex flex-col gap-6">
        <Tarjeta>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-violeta-suave text-violeta">
                <Activity className="size-5" />
              </span>
              <div className="min-w-0">
                <a className="flex items-center gap-1 truncate font-medium text-tinta hover:underline" href={m.url} rel="noreferrer" target="_blank">
                  {m.url.replace(/^https:\/\//, '').replace(/\/$/, '')}
                  <ExternalLink aria-hidden className="size-3.5" />
                </a>
                <p className="text-[13px] text-tenue">Última comprobación: {fechaYHora(m.comprobadaEn)}{m.respuestaMs ? ` · respondió en ${m.respuestaMs} ms` : ''}</p>
              </div>
            </div>
            <Pastilla clave={estado.clave}>{estado.texto}</Pastilla>
          </div>
          {m.caidaAbierta && (
            <div className="mt-4">
              <Aviso tono="error">
                Tu web no responde desde el {fechaYHora(m.caidaAbierta.desde)}: {m.caidaAbierta.causa}. Ya lo sabemos y lo estamos mirando; te escribiremos cuando vuelva.
              </Aviso>
            </div>
          )}
        </Tarjeta>

        <div className="grid gap-4 sm:grid-cols-3">
          {(['24h', '7d', '30d'] as const).map((ventana) => (
            <Tarjeta key={ventana}>
              <p className="text-[13px] text-tenue">{ventana === '24h' ? 'Últimas 24 horas' : ventana === '7d' ? 'Últimos 7 días' : 'Últimos 30 días'}</p>
              <p className="mt-1 font-display text-2xl font-semibold">{porcentaje(m.disponibilidad[ventana]) ?? '—'}</p>
              <p className="text-xs text-tenue">del tiempo en línea</p>
            </Tarjeta>
          ))}
        </div>

        {m.serie.length > 0 && (
          <Tarjeta>
            <h2 className="font-display text-base font-semibold">Tiempo de respuesta, últimas 24 horas</h2>
            <p className="mt-1 text-[13px] text-tenue">Media de las últimas 24 horas: {m.media24hMs ? `${m.media24hMs} ms` : '—'}. Cuanto más bajo, más rápido carga tu web.</p>
            <div aria-label="Tiempo de respuesta por hora" className="mt-4 flex h-28 items-end gap-1" role="img">
              {m.serie.map((t) => (
                <div
                  className={t.fallos > 0 ? 'flex-1 rounded-sm bg-error' : 'flex-1 rounded-sm bg-violeta/70'}
                  key={t.hora}
                  style={{ height: `${Math.max(4, ((t.mediaMs ?? 0) / maximo) * 100)}%` }}
                  title={`${fechaYHora(t.hora)}: ${t.mediaMs ?? '—'} ms${t.fallos ? `, ${t.fallos} fallos` : ''}`}
                />
              ))}
            </div>
          </Tarjeta>
        )}

        <Tarjeta>
          <h2 className="font-display text-base font-semibold">Últimas caídas</h2>
          {m.caidas.length === 0 ? (
            <p className="mt-2 text-[13px] text-tinta-2">Ninguna. Tu web ha respondido siempre que la hemos comprobado.</p>
          ) : (
            <ul className="mt-3 flex flex-col divide-y divide-linea">
              {m.caidas.map((c) => (
                <li className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-[13px]" key={c.desde}>
                  <span>
                    {fechaYHora(c.desde)} · {c.causa}
                  </span>
                  <span className="text-tenue">{c.abierta ? 'sigue sin responder' : `duró ${duracion(c.minutos)}`}</span>
                </li>
              ))}
            </ul>
          )}
        </Tarjeta>
      </div>
    </>
  )
}
